/**
 * Carrusel de la galeria de la ficha de producto.
 *
 * Sustituye al slick que traia woo-variation-gallery. El marcado es el mismo de antes --
 * .woo-variation-gallery-slider-wrapper por fuera y .woo-variation-gallery-slider por dentro --
 * con las clases de Swiper sumadas, asi que el CSS de las tres fichas no cambia.
 *
 * El fade y los 300 ms vienen de lo que hacia el cursor al reconfigurar el slider, y la
 * exclusion de .is-art tambien: en arte la galeria se deja quieta.
 *
 * Al cambiar de variacion, WooCommerce sustituye el nodo entero de la galeria, asi que no
 * basta con inicializar una vez. Se observa el contenedor y se reinicializa sobre el nodo
 * nuevo; el viejo se destruye antes para no dejar listeners sueltos.
 */

import Swiper from 'swiper/bundle';

const CONTENEDOR = '.woo-variation-gallery-slider-wrapper';
const SLIDER = '.woo-variation-gallery-slider';
const WRAPPER = '.woo-variation-gallery-wrapper';
const ARTE = '.is-art';

function opciones(contenedor) {
    const esArte = !!contenedor.closest(ARTE);

    const base = {
        slidesPerView: 1,
        speed: 300,
        /* Sin el loop de Swiper: combinado con el fade se atasca --slideNext() deja de mover
           nada y activeIndex se queda clavado en 0--. El bucle que traia slick de serie se
           rehace en el cursor, que es lo unico que navega esta galeria. */
        loop: false,
        // El cursor paginado del theme hace de flechas; Swiper no pinta las suyas.
        allowTouchMove: true,
        watchOverflow: true,
    };

    /* En arte no se aplica fade: las obras se ven de una en una sin transicion, que es como
       estaba con slick. */
    if (esArte) return base;

    return { ...base, effect: 'fade', fadeEffect: { crossFade: true } };
}

function inicializar(contenedor) {
    if (!contenedor || contenedor.swiper) return contenedor?.swiper ?? null;

    const slider = contenedor.querySelector(SLIDER);

    if (!slider || !slider.children.length) return null;

    const instancia = new Swiper(contenedor, opciones(contenedor));

    /* wvg-ready es lo que destapa la galeria: sin ella el CSS la mantiene oculta para que no
       se vea el salto mientras se monta. */
    contenedor.closest(WRAPPER)?.classList.add('wvg-ready');

    return instancia;
}

function destruir(contenedor) {
    if (contenedor && contenedor.swiper) {
        contenedor.swiper.destroy(true, true);
    }
}

function inicializarTodas(raiz = document) {
    raiz.querySelectorAll(CONTENEDOR).forEach(inicializar);
}

let vigilando = false;

/**
 * El cambio de variacion reemplaza el nodo de la galeria. Se vigila el documento y se monta
 * Swiper sobre lo que aparezca; mirar solo el arranque no vale.
 *
 * Se observa el body y no el nodo del producto: el cambio de modelo por AJAX sustituye ese
 * nodo entero, asi que un observador colgado de el se queda mirando un elemento ya
 * desconectado y la galeria del modelo nuevo nunca llega a montarse. El body siempre esta.
 */
function vigilarCambiosDeVariacion() {
    if (vigilando) return;

    vigilando = true;

    const observador = new MutationObserver((mutaciones) => {
        let hayGaleriaNueva = false;

        mutaciones.forEach((mutacion) => {
            mutacion.removedNodes.forEach((nodo) => {
                if (nodo.nodeType !== 1) return;
                if (nodo.matches?.(CONTENEDOR)) destruir(nodo);
                nodo.querySelectorAll?.(CONTENEDOR).forEach(destruir);
            });

            mutacion.addedNodes.forEach((nodo) => {
                if (nodo.nodeType !== 1) return;
                if (nodo.matches?.(CONTENEDOR) || nodo.querySelector?.(CONTENEDOR)) {
                    hayGaleriaNueva = true;
                }
            });
        });

        if (hayGaleriaNueva) inicializarTodas();
    });

    observador.observe(document.body, { childList: true, subtree: true });
}

export function init_sc_product_gallery() {
    inicializarTodas();
    vigilarCambiosDeVariacion();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init_sc_product_gallery, { once: true });
} else {
    init_sc_product_gallery();
}

window.init_sc_product_gallery = init_sc_product_gallery;
